<script setup lang="ts">
import { computed } from 'vue';

export interface BeliefCardData {
  imageUrl: string;
  titleHtml: string;
  body: string;
  bgColor: string;
}

const props = defineProps<{
  card: BeliefCardData;
}>();

/** sRGB channel 0–1 → linear value per WCAG 2.1 relative luminance */
function linearChannel(u: number): number {
  return u <= 0.03928 ? u / 12.92 : ((u + 0.055) / 1.055) ** 2.4;
}

/**
 * WCAG 2.1 relative luminance (0 = black, 1 = white).
 * Raw sRGB weighting (old implementation) overshoots on saturated mid-tones (e.g. dusty rose, sage),
 * wrongly treating them as “light” and forcing black type.
 */
function relativeLuminance(hex: string): number {
  const raw = typeof hex === 'string' ? hex : '#ebe2db';
  let normalized = raw.replace('#', '').trim();
  if (normalized.length === 3) {
    normalized = normalized.split('').map((c) => c + c).join('');
  }
  if (normalized.length !== 6) return 0.92;
  const r = Number.parseInt(normalized.slice(0, 2), 16) / 255;
  const g = Number.parseInt(normalized.slice(2, 4), 16) / 255;
  const b = Number.parseInt(normalized.slice(4, 6), 16) / 255;
  return 0.2126 * linearChannel(r) + 0.7152 * linearChannel(g) + 0.0722 * linearChannel(b);
}

const bgColorResolved = computed((): string => {
  const c = props.card.bgColor;
  if (typeof c === 'string' && c.trim() !== '') return c;
  return '#ebe2db';
});

/** L > ~0.5 → read as light surface (dark type); muted saturated cards stay lower → light type */
const isLightBg = computed(() => relativeLuminance(bgColorResolved.value) > 0.508);

const titleTextClass = computed(() => (isLightBg.value
  ? 'text-off-black-500 [&_strong]:text-off-black-500'
  : 'text-white [&_strong]:text-white'),
);

const bodyTextClass = computed(() => (isLightBg.value ? 'text-off-black-500' : 'text-white/95'),
);
</script>

<template>
  <article
    class="belief-card flex min-h-[200px] w-full flex-row overflow-hidden rounded-[24px] shadow-sm lg:max-h-[500px] lg:min-h-[420px] lg:flex-col lg:rounded-[20px]"
    :style="{ backgroundColor: bgColorResolved }"
  >
    <!-- Mobile: row (copy | image). Desktop: column (copy top, image fills bottom). -->
    <div
      class="belief-card__copy flex flex-1 flex-col justify-center px-20 py-24 pr-16 lg:flex-none lg:justify-start lg:px-24 lg:pb-16 lg:pt-28"
    >
      <div
        class="text-20 font-semibold leading-125 tracking-tight lg:text-22 [&_p+p]:mt-8 [&_p]:m-0 [&_p]:inline"
        :class="titleTextClass"
        v-html="card.titleHtml"
      />
      <p
        class="leading-135 mt-10 text-14 lg:mt-12"
        :class="bodyTextClass"
      >
        {{ card.body }}
      </p>
    </div>
    <div
      v-if="card.imageUrl"
      class="belief-card__media relative w-[42%] max-w-[200px] shrink-0 self-stretch lg:min-h-[220px] lg:w-full lg:max-w-none lg:flex-1"
    >
      <img
        :src="card.imageUrl"
        alt=""
        class="absolute inset-0 size-full object-cover object-center"
        loading="lazy"
        decoding="async"
      >
    </div>
  </article>
</template>
